/**
 * api_v2/auth.ts
 * Методы аутентификации для API v2.
 * Сервер возвращает session_token — фронт больше не хранит user_id напрямую.
 *
 * ВАЖНО: Эти методы работают ТОЛЬКО когда на сервере создан /v2/auth/
 * До этого момента используется старый RegisterUser.tsx (оригинал не трогаем)
 */

import client, { clearToken, setToken } from "./client";

export interface UserProfile {
  id: number;
  name: string;
  phone: string;
  master_id?: number;
  first_name?: string;
  last_name?: string;
  hasSubscription?: boolean;
}

export interface SendCodeResponse {
  success: boolean;
  message?: string;
  /** ID запроса — передаётся при verify вместо phone */
  request_id?: string;
  method?: "push" | "approval" | "simol_code" | "otp" | "user_call";
  call_number?: string;
  push_token?: string;
  expires_in?: number;
  approval_channel?: "bot" | "push" | "both";
  has_password?: boolean;
}

export interface StartSimolAuthResponse {
  success: boolean;
  message?: string;
  method?: "approval" | "simol_code" | "otp";
  push_token?: string;
  request_id?: string;
  expires_in?: number;
  approval_channel?: "bot" | "push" | "both";
  has_password?: boolean;
}

export interface VerifyCodeResponse {
  success: boolean;
  status?: string;
  message?: string;
  /** JWT / session_token от сервера */
  session_token?: string;
  user?: UserProfile;
  is_new_user?: boolean;
  confirm_token?: string;
  requires_password?: boolean;
  has_password?: boolean;
  auth_token?: string;
}

export interface RegisterUserResponse {
  success: boolean;
  status?: string;
  message?: string;
  session_token?: string;
  user?: UserProfile;
}

export interface PasswordAuthResponse {
  success: boolean;
  status?: string;
  message?: string;
  session_token?: string;
  user?: UserProfile;
}

export type OtpChannel = "voice" | "user_call" | "telegram" | "sms" | "max" | "whatsapp";

export interface AuthOptionsResponse {
  success: boolean;
  phone?: string;
  user_exists?: boolean;
  has_password?: boolean;
  message?: string;
}

export interface SimolContactPayload {
  user_id?: string;
  bot_user_id?: string;
  phone?: string;
  source?: string;
}

export interface SimolLoginResponse {
  success: boolean;
  status?: string;
  message?: string;
  code?: string;
  session_token?: string;
  user?: UserProfile;
}

/** Отправить OTP-код на телефон */
export async function sendCode(
  phone: string,
  channel: OtpChannel = "voice",
  purpose: "login" | "reset" = "login"
): Promise<SendCodeResponse> {
  const { data } = await client.post<SendCodeResponse>("/auth/send_code.php", {
    phone,
    channel,
    purpose,
  });
  return data;
}

export async function getAuthOptions(phone: string): Promise<AuthOptionsResponse> {
  const { data } = await client.post<AuthOptionsResponse>("/auth/auth_options.php", {
    phone,
  });
  return data;
}

export async function checkUserCallAuth(params: {
  phone: string;
  request_id: string;
}): Promise<VerifyCodeResponse & { status?: "pending" | "approved" | "failed" | "expired" }> {
  const { data } = await client.post<
    VerifyCodeResponse & { status?: "pending" | "approved" | "failed" | "expired" }
  >("/auth/check_call_auth.php", params);

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

export async function verifyPasswordResetCode(params: {
  phone: string;
  code: string;
}): Promise<{ success: boolean; message?: string; auth_token?: string }> {
  const { data } = await client.post<{
    success: boolean;
    message?: string;
    auth_token?: string;
  }>("/auth/verify_password_reset_code.php", params);
  return data;
}


export async function startSimolAuth(params: {
  phone: string;
  app_id?: string;
  app_name?: string;
  requester_device_installation_id?: string;
  referral_code?: string;
}): Promise<StartSimolAuthResponse> {
  const { data } = await client.post<StartSimolAuthResponse>("/auth/start_simol_auth.php", {
    phone: params.phone,
    app_id: params.app_id || "simolapps_web",
    app_name: params.app_name || "simolapps.ru",
    requester_device_installation_id: params.requester_device_installation_id,
    referral_code: params.referral_code,
  });
  return data;
}

export async function verifySimolAuth(params: {
  phone: string;
  request_id: string;
  code: string;
  app_id?: string;
  app_name?: string;
  first_name?: string;
  last_name?: string;
  referral_code?: string;
}): Promise<VerifyCodeResponse> {
  const { data } = await client.post<VerifyCodeResponse>("/auth/verify_simol_auth.php", params);
  if (data.success && data.session_token) {
    setToken(data.session_token);
  }
  return data;
}

export async function sendPushAuth(
  phone: string,
  requesterApp = "simolapps_web"
): Promise<SendCodeResponse> {
  const { data } = await client.post<SendCodeResponse>("/auth/send_push_auth.php", {
    phone,
    requester_app: requesterApp,
  });
  return data;
}

export async function checkPushAuth(
  pushToken: string
): Promise<{ success: boolean; status?: "pending" | "approved" | "denied" | "expired"; session_token?: string; message?: string }> {
  const { data } = await client.post<{
    success: boolean;
    status?: "pending" | "approved" | "denied" | "expired";
    session_token?: string;
    message?: string;
  }>("/auth/check_push_auth.php", {
    push_token: pushToken,
  });

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

/**
 * Подтвердить OTP-код.
 * Если успешно — сохраняет token и возвращает профиль пользователя.
 */
export async function verifyCode(
  phone: string,
  code: string,
  requestId?: string
): Promise<VerifyCodeResponse> {
  try {
    const { data } = await client.post<VerifyCodeResponse>("/auth/verify_code.php", {
      phone,
      code,
      ...(requestId ? { request_id: requestId } : {}),
    });

    if (data.success && data.session_token) {
      setToken(data.session_token);
    }

    return data;
  } catch (error: any) {
    console.error("verifyCodeV2 failed", {
      phone,
      status: error?.response?.status,
      message: error?.response?.data?.message || error?.message,
      attempts_remaining: error?.response?.data?.attempts_remaining,
      retry_after: error?.response?.headers?.["retry-after"],
      response: error?.response?.data,
    });
    throw error;
  }
}

/**
 * Зарегистрировать нового пользователя.
 * Вызывается после verifyCode, если is_new_user === true.
 */
export async function registerUser(params: {
  confirm_token: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  password: string;
  referral_code?: string;
}): Promise<RegisterUserResponse> {
  const { data } = await client.post<RegisterUserResponse>("/auth/register_user.php", params);

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

export async function completePasswordAuth(params: {
  auth_token: string;
  password: string;
}): Promise<PasswordAuthResponse> {
  const { data } = await client.post<PasswordAuthResponse>(
    "/auth/complete_password_auth.php",
    params
  );

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

export async function passwordLogin(params: {
  phone: string;
  password: string;
}): Promise<PasswordAuthResponse> {
  const { data } = await client.post<PasswordAuthResponse>(
    "/auth/password_login.php",
    params
  );

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

export async function simolLogin(params: {
  session_token: string;
  contact?: SimolContactPayload;
  referral_code?: string;
}): Promise<SimolLoginResponse> {
  const { data } = await client.post<SimolLoginResponse>("/auth/simol_login.php", params);

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

/**
 * Получить профиль текущего пользователя по токену.
 * Сервер возвращает данные — user_id не передаётся из фронта.
 */
export async function getProfile(): Promise<{ success: boolean; user?: UserProfile }> {
  const { data } = await client.get<{ success: boolean; user?: UserProfile }>("/user/profile.php");
  return data;
}

/**
 * Обменять SSO-токен (из URL ?token=) на сессионный токен.
 * Сразу удаляет SSO-токен из URL (не оставляем в истории браузера).
 */
export async function verifySsoToken(
  ssoToken: string
): Promise<{ success: boolean; session_token?: string; user?: UserProfile }> {
  const { data } = await client.post<{
    success: boolean;
    session_token?: string;
    user?: UserProfile;
  }>("/auth/verify_sso.php", { sso_token: ssoToken });

  if (data.success && data.session_token) {
    setToken(data.session_token);
  }

  return data;
}

export async function registerFcmToken(params: {
  fcm_token: string;
  app_id: string;
  platform: string;
  device_name?: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post<{ success: boolean; message?: string }>(
    "/auth/register_fcm_token.php",
    params
  );
  return data;
}

/** Revoke the current server-side session, then always remove its local copy. */
export async function logoutCurrentSession(): Promise<void> {
  try {
    await client.post("/auth/logout.php", {});
  } finally {
    clearToken();
  }
}
